export const JUDICIAL_PROCESS = [
  {
    id: 'reporting',
    title: '1. Reporting',
    subtitle: 'You decide if, when, and how',
    body:
      'You can report to the police any time after an assault — there is no time limit for reporting rape or sexual offences in England, Wales, Scotland or Northern Ireland. Reporting is your choice. You can dial 999 if you are in immediate danger, 101 for non-emergencies, or attend a Sexual Assault Referral Centre (SARC) for a forensic medical examination without speaking to police first.\n\nYou have the right to be treated with dignity and respect. A specially trained officer (Sexual Offences Liaison Officer / SOIT) should support you throughout. You can request an officer of a specific gender.',
    tips: [
      'You can give a video-recorded interview (ABE) instead of a written statement — it may later be used as your evidence-in-chief in court.',
      'A SARC can preserve forensic evidence for up to 2 years even if you have not decided whether to report.',
      'You can choose to remain anonymous via "third-party reporting" \u2014 not only through Rape Crisis, but also via your GP, another health practitioner, a friend or family member, or any support organisation.',
    ],
  },
  {
    id: 'investigation',
    title: '2. Police Investigation',
    subtitle: 'Gathering evidence',
    body:
      "Police investigate by taking your statement, gathering forensic and digital evidence, and identifying witnesses. You should be kept informed at every key stage. If a suspect is arrested, they may be released under investigation or on bail with conditions.\n\nUnder the Victims' Code, you have the right to be told within 5 working days if a suspect is arrested, charged, released, or bailed.",
    tips: [
      'You can refer yourself for an Independent Sexual Violence Advisor (ISVA) \u2014 a specialist who supports you through the entire process for free. You do not need a referral from the police or anyone else.',
      'You can request a copy of your statement.',
      'Investigations can take months. Ask your officer for regular updates.',
    ],
  },
  {
    id: 'cps',
    title: '3. CPS Decision',
    subtitle: 'Charging decision',
    body:
      "Police pass the file to the Crown Prosecution Service (CPS). A specialist Rape and Serious Sexual Offences (RASSO) prosecutor decides whether to charge, applying the Full Code Test: (1) is there enough evidence for a realistic prospect of conviction, and (2) is prosecution in the public interest.\n\nIf the CPS decides not to charge, you have the right under the Victims' Right to Review (VRR) scheme to ask for the decision to be reviewed.",
    tips: [
      'You can request a meeting with the prosecutor before trial.',
      'If the CPS does not charge, you have 3 months to request a review under VRR.',
    ],
  },
  {
    id: 'court',
    title: '4. Going to Court',
    subtitle: 'Crown Court trial',
    body:
      'Rape cases are heard in the Crown Court before a judge and jury. As the complainant, you are a witness for the prosecution. You will be cross-examined by the defence barrister. The judge controls the questioning and can stop questions that are inappropriate.\n\nYou are entitled to "special measures" to help you give your best evidence: giving evidence behind a screen, by live video link from a separate room, or pre-recorded cross-examination (Section 28). You can also request a court familiarisation visit before the trial.',
    tips: [
      'You have lifetime anonymity from the moment you report — your name cannot be published.',
      'You can have an ISVA or supporter with you at court (but not in the witness box).',
      'Ask the Witness Care Unit about special measures as early as possible.',
    ],
  },
  {
    id: 'verdict',
    title: '5. Verdict',
    subtitle: 'Guilty, not guilty, or hung jury',
    body:
      'The jury of 12 decides the verdict. A "guilty" verdict requires them to be sure beyond reasonable doubt. The verdict is not a comment on whether you were believed — juries can only convict if the legal standard is met on the evidence presented.\n\nA "not guilty" verdict does not mean the jury thought you lied. Many survivors find this stage extremely difficult, regardless of outcome. Support is available either way.',
    tips: [
      'Whatever the verdict, you deserve support afterwards. Reach out to Rape Crisis or your ISVA.',
      'You can choose to receive the verdict in court, by phone, or in writing.',
    ],
  },
  {
    id: 'sentencing',
    title: '6. Sentencing',
    subtitle: 'If found guilty',
    body:
      'If the defendant is convicted, the judge will pass sentence — sometimes immediately, sometimes at a later hearing after pre-sentence reports. Sentencing is guided by the Sentencing Council guidelines.\n\nYou have the right to make a Victim Personal Statement (VPS) explaining how the crime has affected you. It will be read or played to the court before sentencing.',
    tips: [
      'You can write your VPS in your own words and read it aloud, ask someone else to read it, or have it read silently by the judge.',
      'Convicted sex offenders are placed on the Sex Offenders Register.',
    ],
  },
  {
    id: 'appeals',
    title: '7. Appeals & After',
    subtitle: 'What happens next',
    body:
      'The defendant may appeal against conviction or sentence. The Attorney General can refer "unduly lenient" sentences to the Court of Appeal within 28 days. You will be informed if an appeal is lodged.\n\nIf the offender is sentenced to 12+ months for a sexual offence, you can join the Victim Contact Scheme (VCS), which keeps you informed about release, parole hearings and licence conditions, and lets you submit a Victim Personal Statement to parole.',
    tips: [
      'The Victim Contact Scheme is free and run by the Probation Service.',
      'You can ask for licence conditions like exclusion zones to be considered before release.',
    ],
  },
];

export const SCOTLAND_JUDICIAL_PROCESS = [
  {
    id: 'sc-reporting',
    title: '1. Reporting to Police Scotland',
    subtitle: 'You decide if, when, and how',
    body:
      'You can report to Police Scotland at any time — there is no time bar for reporting rape or sexual offences in Scotland. Dial 999 in an emergency, 101 for non-emergencies, or self-refer for a forensic medical exam via NHS 111 or a Sexual Assault Referral service.\n\nEvery Scottish police force has a National Sexual Crime Unit and a National Rape Investigation Unit staffed by trained officers. You have the right to be treated with dignity and to request an officer of a specific gender.',
    tips: [
      "You can be interviewed on video (a Joint Investigative Interview or ABE-style interview) which may later be used as your evidence-in-chief.",
      'A Scottish Forensic Medical Service exam (e.g. Archway in Glasgow) can preserve evidence without you deciding to report to police first.',
      "You can access Rape Crisis Scotland's National Advocacy Project for free, independent support at any stage — you do not need a referral.",
    ],
  },
  {
    id: 'sc-investigation',
    title: '2. Police Investigation',
    subtitle: 'Gathering evidence in Scotland',
    body:
      'Police Scotland investigate by taking your statement, gathering forensic and digital evidence, and identifying witnesses. Suspects may be arrested, released on undertaking (with conditions), or held for court.\n\nYou should be kept informed at every key stage. Under the Standards of Service for Victims and Witnesses you have the right to information about arrest, charge, release and court dates.',
    tips: [
      "You can refer yourself for a Rape Crisis Scotland advocacy worker at any stage — free, confidential and specialists in Scots law.",
      'You can ask for a copy of your statement.',
      'Investigations can take months. Ask your officer or advocacy worker for regular updates.',
    ],
  },
  {
    id: 'sc-copfs',
    title: '3. COPFS Decision',
    subtitle: 'Not the CPS — the Procurator Fiscal decides',
    body:
      "Police pass the file to the Crown Office and Procurator Fiscal Service (COPFS). A specialist prosecutor at the National Sexual Crimes Unit decides whether to prosecute, and in which court (Sheriff Court or High Court). A Procurator Fiscal Depute may interview you again (a 'precognition') to prepare the case.\n\nIf COPFS decides not to prosecute or to drop the case, you have the right to ask for that decision to be reviewed under the Victims' Right to Review scheme.",
    tips: [
      'Corroboration is required in Scots law — a case must be supported by more than one source. Since October 2024, distress observed by a third party can corroborate a rape complaint.',
      'You can request a meeting with the Procurator Fiscal Depute before trial.',
      "If COPFS does not prosecute, you can ask for a review — ask your advocacy worker or COPFS directly.",
    ],
  },
  {
    id: 'sc-court',
    title: '4. Going to Court',
    subtitle: 'Sheriff Court or High Court of Justiciary',
    body:
      "Serious sexual offences (rape, sexual assault by penetration) are prosecuted on indictment at the High Court of Justiciary before a jury of 15. Less serious sexual offences are heard at the Sheriff Court. A new specialist Sexual Offences Court is being phased in under the Victims, Witnesses, and Justice Reform (Scotland) Act 2025.\n\nAs the complainer, you are a witness for the Crown. You will be cross-examined by defence counsel. The judge controls questioning and can stop inappropriate lines. You have automatic anonymity for life.",
    tips: [
      'You have automatic anonymity by law — your name cannot be published in Scotland.',
      'You are entitled to special measures: screens, live TV-link evidence, a supporter beside you, and — in the High Court — pre-recorded evidence so you never share a room with the accused.',
      "You can have a Rape Crisis Scotland advocacy worker with you at court (but not in the witness box).",
    ],
  },
  {
    id: 'sc-verdict',
    title: '5. Verdict — Guilty or Not Guilty',
    subtitle: 'The "not proven" verdict was abolished on 1 January 2026',
    body:
      "From 1 January 2026 Scottish juries can only return Guilty or Not Guilty. A guilty verdict requires a two-thirds majority (10 of 15). The verdict is not a comment on whether you were believed — juries can only convict on the corroborated evidence presented.\n\nA 'not guilty' verdict does not mean the jury thought you lied. Many survivors find this stage extremely difficult, whatever the outcome. Support is available either way.",
    tips: [
      "Whatever the verdict, you deserve support. Reach out to Rape Crisis Scotland or your advocacy worker.",
      'You can choose to receive the verdict in court, by phone, or in writing.',
    ],
  },
  {
    id: 'sc-sentencing',
    title: '6. Sentencing',
    subtitle: 'If found guilty',
    body:
      "If the accused is convicted, the Sheriff or High Court judge passes sentence — sometimes immediately, sometimes after a Criminal Justice Social Work Report. Maximum sentence for rape is life imprisonment. The Sentencing Council for Scotland guidelines apply.\n\nYou have the right to make a Victim Statement describing how the crime has affected you. In High Court and specified solemn Sheriff cases the judge must consider it before sentencing.",
    tips: [
      "You can write your Victim Statement in your own words. The judge must consider it before sentencing.",
      "Convicted sex offenders in Scotland are placed on the Sex Offenders Register and managed under Multi-Agency Public Protection Arrangements (MAPPA).",
    ],
  },
  {
    id: 'sc-appeals',
    title: '7. Appeals & After',
    subtitle: 'What happens next',
    body:
      'The convicted person may appeal against conviction or sentence to the High Court of Justiciary sitting as an Appeal Court. The Lord Advocate can refer unduly lenient sentences for review.\n\nIf the offender is sentenced to prison, you can register for the Victim Notification Scheme (VNS) run by the Scottish Prison Service to be told about release, transfer to open conditions, parole hearings and licence conditions.',
    tips: [
      'The Victim Notification Scheme is free — you can register through mygov.scot or your advocacy worker.',
      'You can ask for licence conditions like exclusion zones to be considered before release.',
      'The Victims and Witnesses Commissioner (2025 Act) is a new independent voice to hold agencies to account.',
    ],
  },
];

export const RIGHTS = [
  {
    id: 'anonymity',
    title: 'Lifetime Anonymity',
    body:
      'From the moment you report a sexual offence, you have lifetime anonymity by law (Sexual Offences (Amendment) Act 1992). Your name, address, photograph or any detail that could identify you cannot be published in the UK. Anyone who breaches this can be prosecuted.',
  },
  {
    id: 'isva',
    title: 'Right to an ISVA',
    body:
      'You have the right to access an Independent Sexual Violence Advisor (ISVA) — a trained specialist who provides emotional and practical support throughout reporting, investigation, trial and beyond. ISVAs are free, independent of the police, and you can refer yourself directly — you do not need to have reported and you do not need anyone else to refer you.',
  },
  {
    id: 'information',
    title: 'Right to be Informed',
    body:
      "Under the Victims' Code of Practice (England & Wales), you have the right to be told within 5 working days of key decisions: arrest, charge, bail, release, court dates, verdict and sentence. You can ask for a single point of contact.",
  },
  {
    id: 'special-measures',
    title: 'Special Measures in Court',
    body:
      'You are automatically eligible for special measures in Crown Court. Options include: giving evidence behind a screen, via live video link from a separate room, having the public gallery cleared, pre-recorded evidence-in-chief (ABE interview), and pre-recorded cross-examination (Section 28).',
  },
  {
    id: 'sexual-history',
    title: 'Protection from Sexual History Questions',
    body:
      'Section 41 of the Youth Justice and Criminal Evidence Act 1999 restricts when the defence can ask about your previous sexual behaviour. The defence must apply to the judge for permission, and such questioning is only allowed in very limited circumstances.',
  },
  {
    id: 'vps',
    title: 'Victim Personal Statement',
    body:
      'You have the right to make a Victim Personal Statement (VPS) describing how the crime has affected you emotionally, physically, financially and socially. It can be considered at sentencing and parole hearings. You can update it at later stages.',
  },
  {
    id: 'review',
    title: 'Right to Review Decisions (VRR)',
    body:
      "If the police decide not to investigate, or the CPS decides not to charge or to stop a prosecution, you have the right to ask for that decision to be reviewed under the Victims' Right to Review schemes. Requests should normally be made within 3 months.",
  },
  {
    id: 'compensation',
    title: 'Criminal Injuries Compensation',
    body:
      'You may be entitled to compensation through the Criminal Injuries Compensation Authority (CICA), even if no one is charged or convicted. Claims must normally be made within 2 years (though there are exceptions, especially for childhood abuse).',
  },
  {
    id: 'support',
    title: 'Right to Specialist Support',
    body:
      'You have the right to be referred to support services — including specialist sexual violence services like Rape Crisis — at any stage. Support is free, confidential, and you do not need to have reported to the police.',
  },
  {
    id: 'complaint',
    title: 'Right to Complain',
    body:
      "If you feel you have not received the rights in the Victims' Code, you can complain to the agency involved (e.g. police, CPS) and, if unresolved, escalate to the Parliamentary and Health Service Ombudsman or the Independent Office for Police Conduct (IOPC).",
  },
];

export const SCOTLAND_RIGHTS = [
  {
    id: 'sc-anonymity',
    title: 'Automatic Lifetime Anonymity',
    body:
      'From the moment you report a sexual offence in Scotland, you have lifetime anonymity by law (Sexual Offences (Amendment) Act 1992, s.1 and s.5). Your name, address, photograph or any detail that could identify you cannot be published. Anyone who breaches this can be prosecuted.',
  },
  {
    id: 'sc-advocacy',
    title: 'Free Specialist Advocacy (NAP)',
    body:
      "You have the right to access free, independent advocacy through Rape Crisis Scotland's National Advocacy Project (NAP). Advocacy workers understand Scots law, Police Scotland and COPFS. Support is confidential, and you can self-refer at any stage — before, during or after reporting.",
  },
  {
    id: 'sc-standards',
    title: 'Standards of Service (2014 Act)',
    body:
      'Under the Victims and Witnesses (Scotland) Act 2014 you have statutory rights to respectful treatment, information about your case, and access to support. The Standards of Service code binds Police Scotland, COPFS, the Scottish Courts and Tribunals Service (SCTS), the Parole Board and the Scottish Prison Service.',
  },
  {
    id: 'sc-special-measures',
    title: 'Special Measures in Court',
    body:
      'Under the Vulnerable Witnesses (Scotland) Act 2004 (strengthened in 2019) you are automatically eligible for special measures: giving evidence behind a screen, by live TV-link, with a supporter beside you, and — in the High Court and the new Sexual Offences Court — pre-recorded evidence so you never share a room with the accused.',
  },
  {
    id: 'sc-victim-statement',
    title: 'Victim Statement',
    body:
      'You have the right to make a Victim Statement in High Court cases and specified solemn Sheriff cases, describing how the crime has affected you emotionally, physically, financially and socially. The judge must consider it before sentencing.',
  },
  {
    id: 'sc-vrr',
    title: "Victims' Right to Review (COPFS)",
    body:
      "If the Procurator Fiscal decides not to prosecute — or to discontinue a case — you have the right to ask for that decision to be reviewed by a senior prosecutor. Your advocacy worker or COPFS can guide you through the application.",
  },
  {
    id: 'sc-vns',
    title: 'Victim Notification Scheme (VNS)',
    body:
      'Run by the Scottish Prison Service. You can register to be told when the person convicted is due to be released, moved to open conditions, or considered for parole. You can register through mygov.scot or via your advocacy worker.',
  },
  {
    id: 'sc-compensation',
    title: 'Criminal Injuries Compensation',
    body:
      'You may be entitled to compensation through the Criminal Injuries Compensation Authority (CICA) — the scheme covers Scotland as well as England and Wales — even if no one is charged or convicted. Claims must normally be made within 2 years (with exceptions, especially for childhood abuse).',
  },
  {
    id: 'sc-commissioner',
    title: 'Victims and Witnesses Commissioner',
    body:
      'The Victims, Witnesses, and Justice Reform (Scotland) Act 2025 creates a new Commissioner to hold Scottish justice agencies to account on victims\u2019 rights and trauma-informed practice.',
  },
  {
    id: 'sc-support',
    title: 'Right to Specialist Support',
    body:
      "You have the right to be referred to specialist services — Rape Crisis Scotland, Scottish Women's Aid, ASSIST, Archway SARC — at any stage. Support is free, confidential, and you do not need to have reported to Police Scotland.",
  },
];

export const SUPPORT_ORGS = [
  {
    id: 'emergency',
    name: 'Emergency Services',
    tagline: 'If you are in immediate danger',
    description:
      'Call 999 if you are in immediate danger or have just been assaulted. If you cannot speak, press 55 when prompted on a mobile to alert the operator silently.',
    phone: '999',
    phoneLabel: 'Call 999',
    hours: '24/7',
    category: 'emergency',
  },
  {
    id: 'police-101',
    name: 'Police Non-Emergency',
    tagline: 'Report or seek advice (non-urgent)',
    description:
      'Call 101 to report an offence that is not happening now, or to speak to your local police force for advice.',
    phone: '101',
    phoneLabel: 'Call 101',
    hours: '24/7',
    category: 'emergency',
  },
  {
    id: 'rape-crisis',
    name: 'Rape Crisis England & Wales',
    tagline: '24/7 Rape & Sexual Abuse Support Line',
    description:
      'Free, confidential specialist support for anyone aged 16+ who has experienced rape, sexual assault or any form of sexual violence at any point in their life. Live chat also available.',
    phone: '08088029999',
    phoneLabel: '0808 802 9999',
    website: 'https://rapecrisis.org.uk',
    hours: '24 hours a day, 365 days a year',
    category: 'helpline',
  },
  {
    id: 'new-pathways',
    name: 'New Pathways',
    tagline: 'Mid, West & South Wales \u2014 sexual violence support',
    description:
      'Wales-based specialist charity providing free, confidential counselling, ISVA support, pre-court support, and SARC services (24/7 medical & forensic care) for survivors of rape and sexual abuse \u2014 children, young people and adults of all genders. Areas covered include Merthyr Tydfil (head office), Rhondda Cynon Taf, Bridgend, Swansea, Carmarthenshire, Cardiff, Newport and Risca, with virtual support available across other parts of Wales. BSL counselling available.',
    phone: '01685379310',
    phoneLabel: '01685 379 310',
    website: 'https://newpathways.org.uk',
    hours: 'Mon\u2013Fri office hours \u00b7 SARCs available 24/7',
    category: 'specialist',
  },
  {
    id: 'cyfannol',
    name: 'Cyfannol Women\u2019s Aid',
    tagline: 'South-East Wales (Gwent) \u2014 sexual violence & domestic abuse',
    description:
      'Trauma-informed, person-led specialist support for survivors of sexual violence and domestic abuse across Gwent (Torfaen, Newport, Blaenau Gwent, Caerphilly, Monmouthshire). Counselling, ISVA, group support and refuge services. For 24/7 access, call the Live Fear Free helpline (0808 80 10 800) and ask to be referred to Cyfannol.',
    phone: '03300564456',
    phoneLabel: '03300 564 456',
    website: 'https://www.cyfannol.org.uk',
    hours: 'Mon\u2013Fri office hours \u00b7 24/7 via Live Fear Free',
    category: 'specialist',
  },
  {
    id: 'calan-dvs',
    name: 'Calan DVS',
    tagline: 'South & West Wales \u2014 sexual violence & domestic abuse',
    description:
      'Specialist support for survivors of sexual violence and domestic abuse across Neath Port Talbot, Carmarthenshire and surrounding areas. Free counselling, ISVA/IDVA support, group work and refuge services for all genders. Out of hours, call the Live Fear Free helpline (0808 80 10 800).',
    phone: '01639633580',
    phoneLabel: '01639 633 580',
    website: 'https://www.calandvs.org.uk',
    hours: 'Mon\u2013Fri office hours \u00b7 24/7 via Live Fear Free',
    category: 'specialist',
  },
  {
    id: 'rasasc-north-wales',
    name: 'RASASC North Wales',
    tagline: 'North Wales \u2014 counselling & ISVA for survivors aged 3+',
    description:
      'Rape & Sexual Abuse Support Centre North Wales \u2014 free, confidential specialist counselling, ISVA support and information for anyone aged 3 and over who has experienced sexual violence or abuse, recent or historic. Covers Anglesey, Gwynedd, Conwy, Denbighshire, Flintshire and Wrexham.',
    phone: '01248670628',
    phoneLabel: '01248 670 628',
    website: 'https://www.rasawales.org.uk',
    hours: 'Mon\u2013Fri, see website for current hours',
    category: 'specialist',
  },
  {
    id: 'stepping-stones-north-wales',
    name: 'Stepping Stones North Wales',
    tagline: 'North Wales \u2014 adult survivors of child sexual abuse',
    description:
      'BACP-registered professional counselling for adult survivors (18+) of childhood sexual abuse \u2014 individual counselling, ISVA support, art therapy, education classes and family support. Free of charge. Covers the six counties of North Wales. Self-referral welcome.',
    phone: '01978352717',
    phoneLabel: '01978 352 717',
    website: 'https://www.steppingstonesnorthwales.co.uk',
    hours: 'Mon\u2013Fri 9:00\u201316:30 \u00b7 24/7 via Live Fear Free',
    category: 'specialist',
  },
  {
    id: 'survivors-trust',
    name: 'The Survivors Trust',
    tagline: 'Helpline for all survivors',
    description:
      'Umbrella agency for specialist rape, sexual violence and childhood sexual abuse support services across the UK and Ireland. Free helpline for adult survivors of any age, gender or background.',
    phone: '08088010818',
    phoneLabel: '0808 801 0818',
    website: 'https://thesurvivorstrust.org',
    hours: 'Mon–Fri, see website for current hours',
    category: 'helpline',
  },
  {
    id: 'victim-support',
    name: 'Victim Support',
    tagline: 'Help for victims of any crime',
    description:
      'Independent national charity offering free, confidential support to anyone affected by crime — whether or not it has been reported and however long ago it happened.',
    phone: '08081689111',
    phoneLabel: '0808 168 9111',
    website: 'https://www.victimsupport.org.uk',
    hours: '24/7 Supportline',
    category: 'helpline',
  },
  {
    id: 'samaritans',
    name: 'Samaritans',
    tagline: 'Someone to talk to, anytime',
    description:
      "Free, confidential listening service for anyone struggling to cope or in emotional distress. You don't have to be suicidal to call.",
    phone: '116123',
    phoneLabel: '116 123',
    website: 'https://www.samaritans.org',
    hours: '24/7',
    category: 'helpline',
  },
  {
    id: 'sarc',
    name: 'SARCs (Sexual Assault Referral Centres)',
    tagline: 'Medical care & forensic services',
    description:
      'SARCs provide free medical care, emotional support, and forensic examination after sexual assault — whether or not you decide to report to the police. Find your nearest SARC on the NHS website.',
    website:
      'https://www.nhs.uk/service-search/other-services/Rape-and-sexual-assault-referral-centres/LocationSearch/364',
    hours: 'Varies by centre',
    category: 'medical',
  },
  {
    id: 'galop',
    name: 'Galop',
    tagline: 'LGBT+ anti-abuse charity',
    description:
      'Specialist support for LGBT+ people who have experienced sexual violence, domestic abuse, hate crime, or so-called "conversion" therapy.',
    phone: '08009995428',
    phoneLabel: '0800 999 5428',
    website: 'https://galop.org.uk',
    hours: 'Mon–Fri, see website',
    category: 'specialist',
  },
  {
    id: 'safeline',
    name: 'Safeline',
    tagline: 'Support for men, women and young people',
    description:
      "National helpline supporting survivors of sexual abuse and rape, including a dedicated men's helpline and young people's service.",
    phone: '08088005005',
    phoneLabel: '0808 800 5005',
    website: 'https://www.safeline.org.uk',
    hours: 'See website for current hours',
    category: 'specialist',
  },
  {
    id: 'survivors-uk',
    name: 'SurvivorsUK',
    tagline: 'For male survivors of sexual violence',
    description:
      'National UK charity supporting boys, men and non-binary people who have experienced any kind of sexual violence — recent or historic. Free web chat, SMS and WhatsApp support, plus specialist ISVA and counselling services.',
    phone: '02035983898',
    phoneLabel: '020 3598 3898',
    website: 'https://www.survivorsuk.org',
    hours: 'See website for chat hours',
    category: 'specialist',
  },
  {
    id: 'into-the-light',
    name: 'Into The Light',
    tagline: 'London \u00b7 non-recent childhood sexual abuse (18+)',
    description:
      'London-based specialist service offering one-to-one counselling, psycho-educational group courses, women\u2019s groups, workshops, information and resources to people of all genders who were sexually abused as a child or young person. Also offers workshops for partners, family and counsellors supporting survivors, and trauma-informed training for community groups and services. Face-to-face in London and online across the UK. For adults 18+. Member of The Survivors Trust. Contact by email at info@intothelight.org.uk.',
    website: 'https://www.intothelight.org.uk',
    hours: 'See website \u00b7 email daily',
    category: 'specialist',
  },
  {
    id: 'supportline',
    name: 'SupportLine',
    tagline: 'Confidential emotional support helpline',
    description:
      'Confidential UK helpline for children, young adults and adults who are isolated, vulnerable, or affected by abuse, sexual violence, domestic violence, bullying, relationship or mental-health difficulties. Registered charity. Also reachable by email and post if the phone is not right for you.',
    phone: '01708765200',
    phoneLabel: '01708 765 200',
    website: 'https://www.supportline.org.uk',
    hours: 'See website for current phone hours \u00b7 email daily',
    category: 'helpline',
  },
];

export const TRAUMA_RESPONSES = [
  {
    id: 'fight',
    letter: 'F',
    name: 'Fight',
    short: 'Push back, shout, hit out',
    body:
      'The body floods with adrenaline. You may shout, struggle, hit, bite or argue. Some survivors describe feeling outside themselves while fighting. Fighting back is not always possible and not fighting is never your fault.',
  },
  {
    id: 'flight',
    letter: 'F',
    name: 'Flight',
    short: 'Run, hide, leave the scene',
    body:
      'The brain pushes the body to move away — running, hiding, or trying to physically escape. After the event, "flight" can also look like avoiding places, people or thoughts that remind you of what happened.',
  },
  {
    id: 'freeze',
    letter: 'F',
    name: 'Freeze',
    short: 'Cannot move or speak',
    body:
      'Often called "tonic immobility." The body becomes still and the voice may disappear, even though the mind is alert. Freezing is extremely common during rape. It is an automatic survival response — NOT consent and not a sign of weakness.',
  },
  {
    id: 'flop',
    letter: 'F',
    name: 'Flop',
    short: 'Go limp, dissociate',
    body:
      'The body goes floppy and muscles relax. You may feel detached, as if you are watching from outside your body (dissociation). The brain is trying to make the experience survivable. This is a protective response, not "letting it happen."',
  },
  {
    id: 'fawn',
    letter: 'F',
    name: 'Fawn',
    short: 'Appease, comply, please',
    body:
      'You may smile, agree, comply, or even act friendly toward the person harming you. Fawning is the brain calculating that compliance is the safest way to survive. It does not mean you wanted it or that you consented.',
  },
];

export const GROUNDING_TECHNIQUES = [
  {
    id: '54321',
    title: '5-4-3-2-1 senses',
    duration: '2 min',
    steps: [
      'Name 5 things you can SEE around you.',
      'Name 4 things you can FEEL (your feet on the floor, fabric on skin).',
      'Name 3 things you can HEAR right now.',
      'Name 2 things you can SMELL — or two smells you like.',
      'Name 1 thing you can TASTE — or a taste you enjoy.',
    ],
  },
  {
    id: 'box',
    title: 'Box breathing',
    duration: '1–3 min',
    steps: [
      'Breathe IN through your nose for 4 slow counts.',
      'HOLD your breath gently for 4 counts.',
      'Breathe OUT through your mouth for 4 counts.',
      'HOLD empty for 4 counts.',
      'Repeat for 4–8 cycles. Stop sooner if it feels uncomfortable.',
    ],
  },
  {
    id: 'orient',
    title: 'Orienting to the room',
    duration: '1 min',
    steps: [
      'Look slowly around you. Move only your eyes, then turn your head.',
      'Say silently: "I am in (place). It is (year). I am (your age)."',
      'Find one object in the room that feels neutral or pleasant.',
      'Notice the temperature of the air on your skin.',
      'Take one slower breath. You are here, now.',
    ],
  },
  {
    id: 'cold',
    title: 'Cold water reset',
    duration: '30 sec',
    steps: [
      'Splash cool water on your face, or hold a cold object (a can, ice cube wrapped in cloth).',
      'Notice the temperature on your hands and face.',
      "Cold activates the body's calming nerve (vagus nerve) and can interrupt panic quickly.",
    ],
  },
];

export const TRAUMA_VIDEOS = [
  {
    id: 'i-just-froze',
    youtubeId: 'O5h-N0N8DaY',
    title: '"I just froze" — Why people freeze during rape',
    source: 'Rape Crisis Scotland',
    duration: '~2 min',
    summary:
      'Animated campaign film explaining freezing as an automatic survival response during sexual violence. Challenges the myth that there is a "right way" to react.',
  },
  {
    id: 'window-of-tolerance',
    youtubeId: 'PtDsHXXZjf4',
    title: 'Understanding your "window of tolerance"',
    source: 'Trauma educator',
    duration: '~4 min',
    summary:
      'Plain-language explainer on how trauma narrows your capacity to cope, why you may swing between hyperarousal (panic, anger) and hypoarousal (shutdown, numbness), and how to come back to centre.',
  },
  {
    id: 'fight-or-flight',
    youtubeId: 'pes7H4ECTdw',
    title: 'How trauma affects the brain and body',
    source: 'Trauma education',
    duration: '~5 min',
    summary:
      'A short, accessible animation showing what happens inside the nervous system during and after a traumatic event — and why "normal" responses can feel anything but normal.',
  },
  {
    id: 'grounding',
    youtubeId: 'TNVlppGz0zM',
    title: 'Grounding techniques for flashbacks & panic',
    source: 'Therapist-led video',
    duration: '~6 min',
    summary:
      'Practical, do-along grounding exercises you can use when you feel overwhelmed, dissociated or have an intrusive memory. No equipment needed.',
  },
];

export const TRAUMA_SERVICES = [
  {
    id: 'gp',
    name: 'Your GP',
    body:
      'Your GP can refer you to NHS talking therapies for trauma-focused CBT or EMDR, prescribe medication if needed, and refer to specialist sexual violence services. You do not need to disclose details — you can simply say you have experienced sexual trauma and want support.',
  },
  {
    id: 'nhs-talking',
    name: 'NHS Talking Therapies (IAPT)',
    body:
      'Free NHS service offering trauma-focused therapy (CBT and, where available, EMDR) for adults in England. In most areas you can self-refer online without going through your GP. Availability varies widely by region \u2014 EMDR is not offered everywhere and wait lists can be long in many parts of the UK, so ask what other trauma-focused therapies your local service can provide in the meantime.',
    website:
      'https://www.nhs.uk/service-search/mental-health/find-a-psychological-therapies-service',
  },
  {
    id: 'rape-crisis-counselling',
    name: 'Rape Crisis Counselling',
    body:
      'Specialist long-term counselling from Rape Crisis centres across England and Wales. Free, confidential, and you do not need to have reported. Find your nearest centre to self-refer.',
    phone: '08088029999',
    phoneLabel: '0808 802 9999',
    website: 'https://rapecrisis.org.uk/get-help/find-a-rape-crisis-centre/',
  },
  {
    id: 'new-pathways-trauma',
    name: 'New Pathways (Wales)',
    body:
      'Trauma-informed counselling, ISVA support, pre-court support and 24/7 SARC services for children, young people and adults of all genders. Areas covered: Merthyr Tydfil, Rhondda Cynon Taf, Bridgend, Swansea, Carmarthenshire, Cardiff, Newport and Risca \u2014 with virtual support across other parts of Wales. BSL counselling available.',
    phone: '01685379310',
    phoneLabel: '01685 379 310',
    website: 'https://newpathways.org.uk',
  },
  {
    id: 'survivors-trust',
    name: 'The Survivors Trust',
    body:
      'UK-wide directory of specialist rape, sexual abuse and childhood sexual abuse therapy services. Helpline can refer you to your nearest member agency.',
    phone: '08088010818',
    phoneLabel: '0808 801 0818',
    website: 'https://thesurvivorstrust.org/find-support',
  },
  {
    id: 'ptsd-uk',
    name: 'PTSD UK',
    body:
      'Charity dedicated solely to PTSD and complex PTSD. Plain-language information on symptoms, NHS-recommended treatments (trauma-focused CBT and EMDR), and how to access them.',
    website: 'https://www.ptsduk.org',
  },
  {
    id: 'mind',
    name: 'Mind',
    body:
      'National mental-health charity. Information on trauma and PTSD, a confidential helpline, and a network of local Minds offering low-cost counselling and peer support.',
    phone: '03001233393',
    phoneLabel: '0300 123 3393',
    website:
      'https://www.mind.org.uk/information-support/types-of-mental-health-problems/post-traumatic-stress-disorder-ptsd-and-complex-ptsd/',
  },
  {
    id: 'samaritans',
    name: 'Samaritans',
    body:
      'If trauma symptoms are overwhelming, or you are having thoughts of harming yourself, Samaritans listen 24/7. You do not have to be in crisis to call.',
    phone: '116123',
    phoneLabel: '116 123',
    website: 'https://www.samaritans.org',
  },
  {
    id: 'khiron-clinics',
    name: 'Khiron Clinics (private trauma treatment)',
    body:
      'Private UK trauma treatment service offering CQC-registered residential and outpatient care for complex trauma, PTSD, dissociation and anxiety. Fees apply \u2014 not free / not NHS. Self-pay or private health insurance. Contact them for fees and availability.',
    phone: '02036681606',
    phoneLabel: '020 3668 1606',
    website: 'https://khironclinics.com',
  },
];

export const POST_TRAUMA_SYMPTOMS = [
  'Intrusive thoughts or flashbacks',
  'Sleep problems & nightmares',
  'Hypervigilance — always on edge',
  'Avoidance of places, people or reminders',
  'Numbness or feeling disconnected',
  'Difficulty concentrating',
  'Strong startle response',
  'Irritability or unexpected anger',
  'Shame, guilt or self-blame',
  'Changes in appetite or how your body feels',
];

/* ===================== DOMESTIC ABUSE ===================== */

export const DA_TYPES = [
  {
    id: 'coercive',
    title: 'Coercive & controlling behaviour',
    body:
      "A pattern of acts designed to make you dependent, isolated, frightened or to deprive you of independence. Since 2015, this is a criminal offence in England & Wales (s.76 Serious Crime Act 2015) with up to 5 years' prison. It can include monitoring your movements, controlling money, dictating who you see, what you wear, what you eat, or making constant threats. You don't need physical injury to report it.",
  },
  {
    id: 'physical',
    title: 'Physical abuse',
    body:
      'Any unwanted physical contact intended to hurt, frighten or control — hitting, slapping, kicking, choking (a specific offence in its own right since 2022), shoving, restraining, denying you sleep or medical care, harming pets to scare you.',
  },
  {
    id: 'sexual',
    title: 'Sexual abuse within a relationship',
    body:
      'Rape, sexual assault, pressured or coerced sex, sharing or threatening to share intimate images, reproductive coercion (controlling contraception or forcing pregnancy/abortion). Marriage and relationships do not change the law — non-consensual sex is rape, full stop.',
  },
  {
    id: 'financial',
    title: 'Economic & financial abuse',
    body:
      "Recognised in law since the Domestic Abuse Act 2021. Includes taking your wages, preventing you from working, running up debt in your name, refusing to let you access your own bank account, hiding household income, or controlling all spending. It's a deliberate trap.",
  },
  {
    id: 'emotional',
    title: 'Emotional & psychological abuse',
    body:
      "Verbal attacks, humiliation, name-calling, gaslighting (making you doubt your memory or sanity), threats to harm you, your children, your pets or themselves, silent treatment, and isolation from family and friends. It leaves no bruises but causes lasting trauma — and it's recognised in UK law.",
  },
  {
    id: 'tech',
    title: 'Stalking, tech & digital abuse',
    body:
      'Following you, repeatedly contacting you, tracking your phone or car, hacking your accounts, installing spyware, demanding passwords, monitoring your social media, sharing or threatening to share intimate images ("revenge porn" \u2014 illegal since 2015). The Protection from Harassment Act 1997 and the Stalking Protection Act 2019 give you specific protections.\n\nFor step-by-step tech-safety guidance \u2014 checking your device for spyware, securing accounts, safe browsing, protecting children\u2019s devices \u2014 Refuge run a dedicated support service: https://refugetechsafety.org.',
  },
  {
    id: 'so-called-honour',
    title: '"Honour"-based abuse, forced marriage & FGM',
    body:
      'Abuse carried out to protect or defend the "honour" of a family or community — including forced marriage (a criminal offence since 2014) and female genital mutilation (FGM). Specialist UK charity Karma Nirvana runs a national helpline (0800 5999 247) and you can apply for a Forced Marriage Protection Order or FGM Protection Order from the family court.',
  },
];

export const DA_LEGAL = [
  {
    id: 'coercive-offence',
    title: 'Coercive control is a criminal offence',
    body:
      'Since 2015, controlling or coercive behaviour in an intimate or family relationship has been a specific criminal offence in England & Wales — even with no physical violence. You can report it to police on 999 (emergency) or 101. From 2023 the offence also covers former partners and family members regardless of whether you still live together.',
  },
  {
    id: 'dapn-dapo',
    title: 'Domestic Abuse Protection Notices & Orders (DAPN / DAPO)',
    body:
      'Replacing the older DVPN/DVPO, DAPNs can be issued by police immediately to keep the perpetrator away from you (typically 48 hours), and DAPOs are longer-term court orders that can ban contact, exclude them from your home, and require behaviour-change programmes. Breaching either is itself a criminal offence.\n\nImportant: DAPNs and DAPOs are currently only available in certain UK areas as part of a phased rollout. Check whether your area is included on the official gov.uk list: https://www.gov.uk/government/publications/domestic-abuse-protection-notices-and-orders. If your area is not yet covered, you can still apply for a non-molestation order or occupation order via NCDV or the family court \u2014 see below.',
  },
  {
    id: 'non-mol',
    title: 'Non-molestation order',
    body:
      "A family-court order banning a partner, ex or family member from harassing, threatening, or contacting you (and your children). NCDV (National Centre for Domestic Violence) can apply for one on your behalf, free, often within 24 hours \u2014 they are specialists in exactly this and are usually faster than a solicitor. You can also apply directly to the family court, and emergency 'without notice' applications can be heard the same day. Breach is a criminal offence carrying up to 5 years' prison.",
  },
  {
    id: 'occupation',
    title: 'Occupation order',
    body:
      'A family-court order deciding who can live in the family home — it can require the abuser to leave, exclude them from a defined area around the property, or let you back in if you have been forced out. Often used alongside a non-molestation order.',
  },
  {
    id: 'clares-law',
    title: "Clare's Law (Domestic Violence Disclosure Scheme)",
    body:
      "You have the right to ask the police whether a current or former partner has a record of abusive behaviour ('right to ask'), and police can proactively tell you if they believe you are at risk ('right to know'). Apply via your local force or online.",
  },
  {
    id: 'sarahs-law',
    title: "Sarah's Law (Child Sex Offender Disclosure Scheme)",
    body:
      'Parents, carers and guardians can ask the police if someone with access to a child has a record of child sexual offences. Police will share information when it is necessary to protect the child.',
  },
  {
    id: 'forced-marriage',
    title: 'Forced Marriage Protection Order',
    body:
      'Forced marriage has been a criminal offence in England, Wales and Scotland since 2014. You (or someone acting for you) can apply for an FMPO from the family court to prevent a forced marriage or remove you from one. The Forced Marriage Unit (FCDO) provides confidential UK-wide advice on 020 7008 0151.',
  },
  {
    id: 'da-act',
    title: 'Domestic Abuse Act 2021 — what changed',
    body:
      'Created a statutory definition of domestic abuse covering physical, sexual, financial, emotional and coercive/controlling behaviour. Children who see, hear or experience the effects are recognised as victims in their own right. Survivors get a statutory right to local-authority refuge support, and DAPN/DAPO replace older orders. Non-fatal strangulation became a specific offence in 2022.',
  },
  {
    id: 'rights-as-victim',
    title: 'Your rights as a victim',
    body:
      "Under the Victims' Code of Practice you have the right to specialist support (IDVAs — Independent Domestic Violence Advisors), to be kept informed at every key stage, to special measures in court (screens, video link, pre-recorded cross-examination), and to make a Victim Personal Statement. You can request a review of police or CPS decisions (VRR).",
  },
];

export const DA_SAFETY_PLAN = {
  intro:
    'A safety plan is a personal, private set of steps that can help keep you and any children safer — whether you are still living with an abuser, planning to leave, or have already left. There is no single right plan. Use what is useful, ignore what is not. If anything below feels risky, skip it.',
  whileLiving: [
    'Identify the rooms in the house with exits and avoid being cornered in the kitchen, bathroom or any room with weapons.',
    'Agree a code word with a trusted friend, neighbour or child that means "call 999 for me".',
    'Teach children that they should not try to intervene — their job is to get to a safe room and call 999.',
    'Keep a charged phone somewhere accessible. If you cannot speak, dial 999 then press 55 (Silent Solution).',
    'If you can, take photos / screenshots of injuries and abusive messages, and email them to yourself or a trusted person.',
  ],
  goBag: [
    'ID: passport, driving licence, birth certificates (yours and children\'s), immigration papers',
    'Money: cash, a spare bank card, building-society book',
    'Keys: house, car, work',
    'Medication and any repeat-prescription details',
    'Phone, charger, and a list of important numbers on paper (in case the phone is taken)',
    'Children\'s favourite toy, a change of clothes for everyone, sanitary items',
    'Court orders, tenancy/mortgage papers, benefit letters, divorce papers',
    'A spare set of photos / memories on a USB stick',
  ],
  leaving: [
    'Refuge UK offers emergency refuge places via the 24-hour National Domestic Abuse Helpline (0808 2000 247) — they can talk you through options without you needing to commit.',
    'You can call 999 and ask to be taken to a refuge — the police will help.',
    "Tell only people you trust 100%. Abusers often have access to phones, emails and bank accounts — set up a new email address from a device they don't use.",
    'Change passwords for email, banking, social media; turn off location sharing on your phone (Find My, Google location, shared calendars).',
    'If you have children, take their red book / NHS records if you can — but your safety comes first; these can all be replaced.',
    "Apply for non-molestation and occupation orders via NCDV (National Centre for Domestic Violence, 0800 970 2070) \u2014 they are DA specialists, can act same-day and it is free. A solicitor or family court are alternatives; legal aid is usually available for domestic-abuse survivors.",
  ],
  afterLeaving: [
    'Vary your routine — different routes to work, school, shops.',
    "Tell your child's school, your GP, your workplace and any building security about the situation and share a photo of the abuser if helpful.",
    'Consider a personal-safety app (Hollie Guard, bSafe) and the police "Silent Solution" — dial 999 then press 55.',
    'Change locks; consider window alarms, a Ring-style camera, smart-bulb timers.',
    'Keep copies of any breach (texts, emails, sightings) — police can act on a breach of a non-molestation order or DAPO.',
    "Look after yourself: refuge counsellors, IDVAs, NHS talking therapies — you don't need to do this alone.",
  ],
};

export const DA_ORGS = [
  {
    id: 'refuge-helpline',
    name: 'National Domestic Abuse Helpline (Refuge)',
    tagline: '24/7, free, confidential',
    description:
      'Run by Refuge — the UK\'s largest specialist DV charity. Available 24/7 every day of the year. They can talk you through options, find a refuge place, or just listen. Translators available.',
    phone: '08082000247',
    phoneLabel: '0808 2000 247',
    website: 'https://www.nationaldahelpline.org.uk',
    hours: '24/7',
    category: 'helpline',
  },
  {
    id: 'womens-aid',
    name: "Women's Aid",
    tagline: 'Live Chat & Survivors\' Forum',
    description:
      'Federation of over 170 local services. Free Live Chat (Mon–Fri 10am–6pm, Sat–Sun 10am–6pm), Survivors\' Forum (24/7), and the Survivor\'s Handbook — practical, written-by-survivors guidance.',
    website: 'https://www.womensaid.org.uk',
    hours: 'Live chat: 10am–6pm daily',
    category: 'specialist',
  },
  {
    id: 'live-fear-free',
    name: 'Live Fear Free (Welsh Women\u2019s Aid)',
    tagline: 'Wales-wide \u2014 24/7, in Welsh or English',
    description:
      'The Welsh Government\u2019s free, confidential 24/7 helpline run by Welsh Women\u2019s Aid. Open to anyone in Wales affected by domestic abuse, sexual violence or other forms of violence against women \u2014 women, men, non-binary, family members, friends, and professionals. Available in Welsh, English, and via translators in over 100 languages. SMS, web chat and email options available.',
    phone: '08088010800',
    phoneLabel: '0808 80 10 800',
    website: 'https://welshwomensaid.org.uk/what-we-do/live-fear-free-helpline/',
    hours: '24 hours a day, 7 days a week',
    category: 'helpline',
  },
  {
    id: 'mens-advice-line',
    name: "Men's Advice Line",
    tagline: 'For male victims of domestic abuse',
    description:
      'Free, confidential helpline run by Respect for men experiencing abuse from a partner, ex-partner or family member — including gay, bi and trans men.',
    phone: '08088010327',
    phoneLabel: '0808 801 0327',
    website: 'https://mensadviceline.org.uk',
    hours: 'Mon–Fri 10am–8pm',
    category: 'specialist',
  },
  {
    id: 'mankind-initiative',
    name: 'Mankind Initiative',
    tagline: 'UK charity for male victims of domestic abuse',
    description:
      'Confidential helpline supporting men in England, Scotland and Wales experiencing domestic abuse or coercive control from their partner, ex-partner or family member. Provides emotional and practical support, signposting to refuges and legal advice.',
    phone: '01823334244',
    phoneLabel: '01823 334244',
    website: 'https://mankind.org.uk',
    hours: 'Mon–Fri 10am–4pm',
    category: 'specialist',
  },
  {
    id: 'survivors-uk-da',
    name: 'SurvivorsUK',
    tagline: 'Male survivors of sexual abuse & relationship abuse',
    description:
      'National UK charity supporting boys, men and non-binary people who have experienced any form of sexual violence — including within an abusive relationship. Free web chat, SMS and WhatsApp support, plus specialist ISVA / IDVA and counselling.',
    phone: '02035983898',
    phoneLabel: '020 3598 3898',
    website: 'https://www.survivorsuk.org',
    hours: 'See website for chat hours',
    category: 'specialist',
  },
  {
    id: 'karma-nirvana',
    name: 'Karma Nirvana',
    tagline: '"Honour"-based abuse & forced marriage',
    description:
      'National UK helpline supporting victims of "honour"-based abuse and forced marriage. Operated by survivors. Also supports professionals working with affected communities.',
    phone: '08005999247',
    phoneLabel: '0800 5999 247',
    website: 'https://karmanirvana.org.uk',
    hours: 'Mon–Fri 9am–5pm',
    category: 'specialist',
  },
  {
    id: 'fcdo-forced-marriage',
    name: 'Forced Marriage Unit (FCDO)',
    tagline: 'UK government — UK & overseas',
    description:
      'Joint Foreign & Commonwealth Office and Home Office unit providing confidential advice and support to people at risk of forced marriage, both in the UK and overseas.',
    phone: '02070080151',
    phoneLabel: '020 7008 0151',
    website: 'https://www.gov.uk/guidance/forced-marriage',
    hours: 'Mon–Fri 9am–5pm (24/7 for emergencies)',
    category: 'specialist',
  },
  {
    id: 'ncdv',
    name: 'NCDV',
    tagline: 'Free same-day legal orders',
    description:
      'National Centre for Domestic Violence helps you apply for emergency non-molestation, occupation and prohibited-steps orders — often within 24 hours. Free, regardless of financial circumstances.',
    phone: '08009702070',
    phoneLabel: '0800 970 2070',
    website: 'https://www.ncdv.org.uk',
    hours: '24/7 helpline',
    category: 'specialist',
  },
  {
    id: 'galop-da',
    name: 'Galop — LGBT+ domestic abuse',
    tagline: 'For LGBT+ survivors',
    description:
      'Specialist UK LGBT+ anti-abuse charity. Free, confidential domestic abuse helpline staffed by LGBT+ people, for LGBT+ people experiencing domestic abuse, sexual violence, hate crime or so-called "conversion" therapy.',
    phone: '08009995428',
    phoneLabel: '0800 999 5428',
    website: 'https://galop.org.uk',
    hours: 'Mon–Fri, see website',
    category: 'specialist',
  },
  {
    id: 'hourglass',
    name: 'Hourglass',
    tagline: 'Abuse of older people',
    description:
      "The only UK-wide charity dedicated to safer ageing. Free, confidential helpline for anyone (any age) concerned about the safety, dignity or finances of an older person — including coercion, neglect, theft and 'romance' scams.",
    phone: '08088088141',
    phoneLabel: '0808 808 8141',
    website: 'https://wearehourglass.org',
    hours: 'Mon–Fri 9am–5pm',
    category: 'specialist',
  },
  {
    id: 'respect-phoneline',
    name: 'Respect Phoneline',
    tagline: 'For people worried about their own behaviour',
    description:
      'If you are concerned that your own behaviour is hurting your partner or family, this is a confidential, non-judgemental helpline that can help you change. Asking for help is a courageous step.',
    phone: '08088024040',
    phoneLabel: '0808 802 4040',
    website: 'https://respectphoneline.org.uk',
    hours: 'Mon–Fri 10am–5pm',
    category: 'specialist',
  },
  {
    id: 'refuge-tech-safety',
    name: 'Refuge Tech Safety',
    tagline: 'Free UK support for tech-facilitated abuse',
    description:
      'Dedicated Refuge service helping survivors deal with tech abuse \u2014 stalkerware / spyware, tracking, image-based abuse, hacked accounts, safe browsing, protecting children\u2019s devices. Free step-by-step guides plus a specialist chat and phone service.',
    website: 'https://refugetechsafety.org',
    hours: 'See website for current phone / chat hours',
    category: 'specialist',
  },
  {
    id: 'supportline-da',
    name: 'SupportLine',
    tagline: 'Confidential emotional support helpline',
    description:
      'Confidential UK helpline for children, young adults and adults who are isolated, vulnerable, or affected by domestic violence, abuse, bullying, relationship or mental-health difficulties. Registered charity. Also reachable by email and post if the phone is not right for you.',
    phone: '01708765200',
    phoneLabel: '01708 765 200',
    website: 'https://www.supportline.org.uk',
    hours: 'See website for current phone hours \u00b7 email daily',
    category: 'helpline',
  },
];

/* ===================== DOMESTIC ABUSE — JUDICIAL PROCESS ===================== */

export const DA_PROCESS = [
  {
    id: 'recognising',
    title: '1. Recognising abuse',
    subtitle: 'You don\'t have to be sure to ask for help',
    body:
      "If something doesn\u2019t feel right \u2014 if you walk on eggshells, feel watched, controlled, isolated, frightened, demeaned, or financially trapped \u2014 you can talk it through anonymously with a specialist. You do not need bruises, you do not need certainty, and you do not need a label. Helpline workers are trained to listen without pressure.",
    tips: [
      'Refuge\u2019s 24/7 National Domestic Abuse Helpline (0808 2000 247) and Women\u2019s Aid Live Chat will not record your name or call.',
      'Coercive control became a criminal offence in 2015 — emotional, financial and digital abuse all count.',
      'You can be referred to an IDVA (Independent Domestic Violence Advisor) without going to the police.',
    ],
  },
  {
    id: 'safety',
    title: '2. Planning for safety',
    subtitle: 'Your safety comes before reporting',
    body:
      "Before any contact with police, courts or family, a safety plan reduces risk. An IDVA, refuge worker or specialist helpline can help you build one \u2014 covering escape routes, a \u2018go bag\u2019, code words, what to take, and what to do if you can\u2019t leave today. Refuge can also arrange an emergency safe house if needed.",
    tips: [
      'If you can\u2019t speak on a 999 call, press 55 \u2014 the Silent Solution alerts the operator.',
      'Set up a new email account from a device the abuser doesn\u2019t have access to.',
      'Change passwords; turn off location-sharing on phones and shared accounts.',
    ],
  },
  {
    id: 'reporting',
    title: '3. Reporting (if and when you choose)',
    subtitle: 'You decide whether and when to involve police',
    body:
      "Reporting to the police is your choice \u2014 you do not need to have reported to access domestic-abuse support. You can self-refer directly to any specialist DA service (Refuge, Women\u2019s Aid, your local IDVA/IDVA service) and they will triage you into an IDVA or lower-risk support locally \u2014 no police involvement required.\n\nIf you do want to report: call 999 in immediate danger, 101 for non-emergency, or attend any police station. You can also do third-party reporting via an IDVA, GP or specialist service. There is no time limit on reporting most domestic-abuse offences. You can request an officer of a specific gender and take a supporter with you.",
    tips: [
      'You can give a video-recorded interview (ABE) instead of a written statement \u2014 this may later be used as your evidence in court.',
      'Ask for the Public Protection Unit \u2014 specially trained officers handle DA cases.',
      'You can withdraw a statement later \u2014 but the CPS may still continue the case if there is enough other evidence.',
    ],
  },
  {
    id: 'protective-orders',
    title: '4. Immediate protection',
    subtitle: 'DAPN, DAPO, non-molestation, occupation orders',
    body:
      "Police can issue a Domestic Abuse Protection Notice (DAPN) on the spot to keep the abuser away from you for up to 48 hours, then apply to court for a Domestic Abuse Protection Order (DAPO) lasting much longer. You can also apply to the family court yourself \u2014 emergency \u2018without-notice\u2019 non-molestation orders can be granted the same day. An occupation order can require the abuser to leave the family home. Breach of any of these is a criminal offence.",
    tips: [
      'Legal aid is normally available for DV survivors regardless of income \u2014 NCDV (0800 970 2070) helps you apply in 24 hours, free.',
      'Bring any photos, texts, voicemails, A&E records or witness statements to your application.',
      'A non-molestation order can also protect any children in the household.',
    ],
  },
  {
    id: 'investigation',
    title: '5. Police investigation',
    subtitle: 'Gathering evidence',
    body:
      "Police will take statements, collect digital evidence (texts, call logs, location data), photograph injuries, interview witnesses, and may make an arrest. A DASH risk-assessment helps determine the level of danger and what support you need. You will normally be allocated a named officer and have the right to be updated within 5 working days of key decisions under the Victims\u2019 Code.",
    tips: [
      'Ask for a Multi-Agency Risk Assessment Conference (MARAC) referral if your risk is high \u2014 police, council, NHS and refuges plan around your safety together.',
      'You have the right to a copy of your statement.',
      'You can be referred to an IDVA for the duration of the case.',
    ],
  },
  {
    id: 'cps',
    title: '6. CPS charging decision',
    subtitle: 'Decision to prosecute',
    body:
      "If police find enough evidence, they pass the case to the Crown Prosecution Service. The CPS applies the Full Code Test: (1) is there a realistic prospect of conviction, and (2) is prosecution in the public interest. DV cases have a strong presumption in favour of prosecution, even where the victim no longer wishes to support the case (\u2018evidence-led\u2019 prosecution), where there is enough other evidence. If the CPS decides not to charge, you have the right to ask for that decision to be reviewed (VRR).",
    tips: [
      'You can request a meeting with the prosecutor before trial.',
      'You can ask the CPS not to disclose your new address to the defence.',
      'VRR requests should normally be made within 3 months.',
    ],
  },
  {
    id: 'court',
    title: '7. Court',
    subtitle: 'Magistrates or Crown Court',
    body:
      "Most DA offences start at the Magistrates Court. More serious ones (including coercive control, non-fatal strangulation, rape) go to the Crown Court before a judge and jury. You are entitled to \u2018special measures\u2019: screens in court, evidence by live video link, pre-recorded evidence and pre-recorded cross-examination (Section 28). The Witness Care Unit will keep you informed of dates. Specialist Domestic Abuse Courts and Independent Domestic Violence Advisors (IDVAs) support you throughout.",
    tips: [
      'You can request a court familiarisation visit before the trial.',
      'You can have an IDVA or supporter with you (but not in the witness box).',
      'Cross-examination by the abuser in person is banned in DA cases \u2014 the court appoints an advocate to do this instead.',
    ],
  },
  {
    id: 'after',
    title: '8. After the case',
    subtitle: 'Verdict, sentence, recovery',
    body:
      "Sentencing can range from rehabilitation programmes (such as Building Better Relationships) and restraining orders to long custodial sentences for serious offences \u2014 coercive control carries up to 5 years, non-fatal strangulation up to 5 years, and most contact offences are aggravated when committed against a partner. You have the right to make a Victim Personal Statement before sentencing. If the offender gets 12 months or more, you can join the Victim Contact Scheme (VCS) to be told of release and licence conditions.\n\nWhatever the outcome, recovery support remains available: an IDVA, community counselling via specialist DA services or The Survivors Trust, and NHS trauma-focused CBT or EMDR (self-refer via NHS Talking Therapies where you live \u2014 note EMDR availability varies widely across the UK and wait lists can be long, so it is worth asking about interim support). Some refuges also offer in-house counselling but this is not universal \u2014 ask your local service what they provide.",
    tips: [
      'A restraining order can be made even on acquittal if the court is satisfied it is needed.',
      'You can apply to the Victim Contact Scheme any time after a 12+ month sentence is passed.',
      'Criminal Injuries Compensation (CICA) may be available even if no one is convicted.',
    ],
  },
];

/* ===================== DOMESTIC ABUSE — RIGHTS ===================== */

export const DA_RIGHTS = [
  {
    id: 'da-act-2021',
    title: 'You are recognised in law',
    body:
      "The Domestic Abuse Act 2021 sets a statutory definition of domestic abuse covering physical, sexual, financial, emotional and coercive/controlling behaviour by anyone you are personally connected to. Children who see, hear or experience its effects are recognised as victims in their own right. This is the legal foundation for every right below.",
  },
  {
    id: 'da-coercive-control',
    title: 'Coercive control is a criminal offence',
    body:
      "Section 76 of the Serious Crime Act 2015 makes controlling or coercive behaviour in an intimate or family relationship a criminal offence in England & Wales, with up to 5 years\u2019 prison. Since 2023 it also applies to ex-partners and family members regardless of whether you still live together.",
  },
  {
    id: 'da-protective-orders',
    title: 'Right to protective orders',
    body:
      "You have the right to apply for non-molestation and occupation orders in the family court \u2014 often the same day on a \u2018without notice\u2019 basis. Police can also issue immediate DAPNs and apply for DAPOs that ban contact, exclude an abuser from the home, and require behaviour-change programmes. Breach of any of these is itself a criminal offence.",
  },
  {
    id: 'da-legal-aid',
    title: 'Right to legal aid',
    body:
      "Legal aid is normally available to survivors of domestic abuse regardless of income to apply for family-court orders. NCDV (0800 970 2070) can prepare and file applications within 24 hours, free.",
  },
  {
    id: 'da-clares-law',
    title: 'Right to ask & right to know (Clare\u2019s Law)',
    body:
      "Under the Domestic Violence Disclosure Scheme you can ask police whether a current or former partner has a history of abusive behaviour (\u2018right to ask\u2019). Police can also proactively warn you when they believe you may be at risk (\u2018right to know\u2019).",
  },
  {
    id: 'da-idva',
    title: 'Right to a specialist advocate (IDVA)',
    body:
      "You have the right to be supported by an Independent Domestic Violence Advisor \u2014 a trained specialist who supports you through safety planning, courts, housing, benefits and recovery. IDVAs are independent of the police, free, and you don\u2019t have to have reported.",
  },
  {
    id: 'da-refuge',
    title: 'Right to refuge and emergency housing',
    body:
      "The Domestic Abuse Act 2021 places a statutory duty on local authorities to provide safe accommodation for survivors and their children. Refuge or the National DA Helpline (0808 2000 247) can find an emergency space, 24/7.",
  },
  {
    id: 'da-special-measures',
    title: 'Special measures in court',
    body:
      "Survivors of domestic abuse are automatically eligible for special measures: screens in the witness box, video link from a separate room, pre-recorded cross-examination (Section 28), and removal of wigs and gowns. Cross-examination by the abuser in person is banned.",
  },
  {
    id: 'da-vps',
    title: 'Victim Personal Statement & contact scheme',
    body:
      "You have the right to make a Victim Personal Statement before sentencing, describing how the abuse has affected you. If the offender receives a 12-month sentence or more, you can join the Victim Contact Scheme to be told about release, licence conditions and parole hearings, and to submit a further statement.",
  },
  {
    id: 'da-vrr',
    title: 'Right to review decisions (VRR)',
    body:
      "If police or the CPS decide not to investigate or prosecute, you have the right to ask for that decision to be reviewed under the Victims\u2019 Right to Review schemes. Requests should normally be made within 3 months.",
  },
  {
    id: 'da-cica',
    title: 'Criminal Injuries Compensation (CICA)',
    body:
      "You may be entitled to compensation through CICA for physical or psychological injury \u2014 even if no one is charged or convicted. Claims should usually be made within 2 years (longer in childhood-abuse cases).",
  },
  {
    id: 'da-children',
    title: 'Children\u2019s rights',
    body:
      "Children who see, hear or experience the effects of domestic abuse are victims in their own right under the 2021 Act. They have a right to specialist support, to be safeguarded by the local authority, and to be heard in family-court decisions about contact and residence.",
  },
];

/* ===================== ORG GROUPS ===================== */
/* Splits the Support page into two clear topics. */

export const SV_ORG_IDS = [
  'emergency',
  'police-101',
  'rape-crisis',
  'new-pathways',
  'cyfannol',
  'calan-dvs',
  'rasasc-north-wales',
  'stepping-stones-north-wales',
  'survivors-trust',
  'victim-support',
  'samaritans',
  'sarc',
  'galop',
  'safeline',
  'survivors-uk',
  'into-the-light',
  'supportline',
];


